/* eslint-disable no-undef */
import { POWERED_BY_SORT_ORDER } from '@mm/constants/widgets';
import ButtonModel, {
  ButtonRequestModel,
  ButtonType,
} from '@mm/state/model/ButtonModel';
import { MainMenuItemModel } from '@mm/state/model/MainMenuModel';
import {
  closeModal,
  openMenuButtonConfigFormModal,
} from '@mm/state/modules/modalsState/actions';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { cloneDeep, get, omit } from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import Button from '../../../../../../common/buttons/DeprecatedButton/DeprecatedButton';
import {
  SortableContainer,
  SortableElement,
} from '../../../../../../common/lib/react-sortable-hoc';
import { makeButton } from '../../../requestModels/widgets/buttonStackWidget/Button';
import { ButtonComponent } from '../buttons/ButtonComponent/ButtonComponent';
import './MenuButtonListEditor.pcss';

const ButtonsContainer = SortableContainer(
  ({ children, ...props }: { [key: string]: any }) => (
    <div {...props}>{children}</div>
  ),
);

// @ts-ignore
const mapButtonRequestToMainMenuItemRequest: (
  button: ButtonRequestModel,
  pageId: number | null,
) => Omit<MainMenuItemModel, 'id'> = (button, pageId) => {
  const { sortOrder, type } = button;
  const gotoPageId = get(button, 'config.pageId');
  const rawButton = {
    sortOrder,
    type: type === ButtonType.URL ? 'web_url' : 'postback',
    title: button.name,
    url: get(button, 'config.url'),
    payload: gotoPageId ? `${button.name}|GOTO_PAGE_ID:${gotoPageId}` : null,
    webviewHeightRatio: 'full',
    pageId,
  };
  return omit(rawButton, type === ButtonType.URL ? 'payload' : 'url');
};

const mapButtonToMainMenuItem: (
  button: ButtonModel,
  pageId: number | null,
) => MainMenuItemModel = (button, pageId) => {
  return {
    ...mapButtonRequestToMainMenuItemRequest(
      { ...button, name: button.name || '' },
      pageId,
    ),
    id: button.id,
  };
};

const DnDButtonComponent = SortableElement(ButtonComponent);

const mapStateToProps = (state: StoreState) => ({
  activeChatbotId: activeChatbotIdSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  openMenuButtonConfigFormModal: (
    button: ButtonModel | ButtonRequestModel,
    buttonIndex: number,
    onCreateButton: (button: ButtonRequestModel) => void,
    onUpdateButton: (buttonIndex: number, button: ButtonModel) => void,
    closeModalOverride?: () => void,
  ) =>
    dispatch(
      openMenuButtonConfigFormModal(
        button,
        buttonIndex,
        onCreateButton,
        onUpdateButton,
        closeModalOverride,
      ),
    ),
  closeModal: () => dispatch(closeModal()),
});

type OuterProps = {
  pageId?: number | null;
  buttons: ButtonModel[];
  maxButtonCount: number;
  onCreateButton: (
    chatbotId: number,
    menuItem: Omit<MainMenuItemModel, 'id'>,
  ) => Promise<any>;
  onDeleteButton: (
    chatbotId: number,
    menuItem: MainMenuItemModel,
  ) => Promise<any>;
  onUpdateButton: (
    chatbotId: number,
    menuItem: MainMenuItemModel,
  ) => Promise<any>;
  closeModalOverride?: () => void;
};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

type Props = OuterProps & InnerProps;

type State = {
  buttonLabel: string;
  selectedPageId: number;
};

class MenuButtonListEditor extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      buttonLabel: '',
      selectedPageId: -1,
    };
  }

  static defaultProps = {
    buttons: [],
  };

  sortedButtons: ButtonModel[] = [];

  getMaxSortOrder = (buttons: ButtonModel[]) => {
    // We need to skip unremovable Powered by MobileMonkey button for Main Menu
    const buttonsToMap = buttons.filter(
      b => b.sortOrder !== POWERED_BY_SORT_ORDER,
    );

    return Math.max(...buttonsToMap.map(b => b.sortOrder), 0);
  };

  onCreateButton = (button: ButtonRequestModel) => {
    const { buttons, activeChatbotId, pageId = null } = this.props;
    const maxSortOrder = this.getMaxSortOrder(buttons);

    this.props.onCreateButton(activeChatbotId, {
      ...mapButtonRequestToMainMenuItemRequest(button, pageId),
      sortOrder: maxSortOrder + 1,
    });

    this.props.closeModalOverride?.() || this.props.closeModal();
  };

  onUpdateButton = (index: number, button: ButtonModel) => {
    const { activeChatbotId, pageId = null } = this.props;
    this.props.onUpdateButton(
      activeChatbotId,
      mapButtonToMainMenuItem(button, pageId),
    );

    this.props.closeModalOverride?.() || this.props.closeModal();
  };

  onClickButton = (index: number) => () => {
    const { openMenuButtonConfigFormModal, closeModalOverride } = this.props;
    const selectedButton = { ...this.sortedButtons[index] };

    openMenuButtonConfigFormModal(
      selectedButton,
      index,
      this.onCreateButton,
      this.onUpdateButton,
      closeModalOverride,
    );
  };

  onClickAddButton = () => {
    const { openMenuButtonConfigFormModal, closeModalOverride } = this.props;
    const maxSortOrder = this.getMaxSortOrder(this.props.buttons);
    const selectedButton = makeButton({
      type: ButtonType.PAGE,
      name: '',
      sortOrder: maxSortOrder + 1,
      configExtendObject: {
        pageId: -1,
      },
    });
    const selectedButtonIndex = -1;

    openMenuButtonConfigFormModal(
      selectedButton,
      selectedButtonIndex,
      this.onCreateButton,
      this.onUpdateButton,
      closeModalOverride,
    );
  };

  onDeleteButton = (index: number) => () => {
    const { activeChatbotId, pageId = null } = this.props;
    this.props.onDeleteButton(
      activeChatbotId,
      mapButtonToMainMenuItem(this.props.buttons[index], pageId),
    );
  };

  onSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    const { activeChatbotId, pageId = null } = this.props;
    if (oldIndex === newIndex) return;

    const buttons = [...this.sortedButtons];

    const poweredByIndex = buttons.findIndex(
      b => b.sortOrder === POWERED_BY_SORT_ORDER,
    );
    if (poweredByIndex > -1) {
      if (newIndex === poweredByIndex || oldIndex === poweredByIndex) return;
    }

    const { sortOrder: newSortOrder } = buttons[newIndex];
    const targetButton = { ...buttons[oldIndex] };

    this.props.onUpdateButton(
      activeChatbotId,
      mapButtonToMainMenuItem(
        {
          ...targetButton,
          sortOrder: newSortOrder,
        },
        pageId,
      ),
    );

    if (oldIndex < newIndex) {
      for (let i = oldIndex + 1; i <= newIndex; i += 1) {
        const button = buttons[i];

        this.props.onUpdateButton(
          activeChatbotId,
          mapButtonToMainMenuItem(
            {
              ...button,
              sortOrder: button.sortOrder - 1,
            },
            pageId,
          ),
        );
      }
    }

    if (oldIndex > newIndex) {
      for (let i = newIndex; i < oldIndex; i += 1) {
        const button = buttons[i];

        this.props.onUpdateButton(
          activeChatbotId,
          mapButtonToMainMenuItem(
            {
              ...button,
              sortOrder: button.sortOrder + 1,
            },
            pageId,
          ),
        );
      }
    }
  };

  renderButtons = () => {
    const { buttons } = this.props;
    const copiedButtons = buttons.map(b => cloneDeep(b));
    copiedButtons.sort((a, b) => a.sortOrder - b.sortOrder);

    this.sortedButtons = copiedButtons;

    return copiedButtons.map((button, index) => (
      <div className="menu-button-list-editor__button-container">
        <DnDButtonComponent
          index={index}
          renderDragIcon={true}
          className="menu-button-list-editor__button"
          useFancyTrashIcon={true}
          onClick={this.onClickButton(index)}
          onDelete={this.onDeleteButton(index)}
          entity={button}
          mainMenuSelected={true}
        />
      </div>
    ));
  };

  render() {
    const canAddButton =
      this.props.buttons.length < (this.props.maxButtonCount || Infinity);
    const buttons = this.renderButtons();
    // @ts-ignore Pretty sure this prop does not exist but ignore for now
    const tooltipId = this.props.widget && `widget-${this.props.widget.id}`;

    return (
      <div className="menu-button-list-editor__buttons-outer-container">
        <ButtonsContainer
          className="menu-button-list-editor__buttons-container"
          lockAxis="y"
          onSortEnd={this.onSortEnd}
          distance={15}
          lockToContainerEdges
          lockOffset={0}
        >
          {buttons}
        </ButtonsContainer>
        {canAddButton ? (
          <div
            className="menu-button-list-editor__buttons-inner-container"
            id={tooltipId}
          >
            <Button
              purple
              invert
              block
              className="menu-button-list-editor__add-button"
              onClick={this.onClickAddButton}
              disabled={!canAddButton}
            >
              + Add button
            </Button>
          </div>
        ) : null}
      </div>
    );
  }
}

const enhance = compose(connect(mapStateToProps, mapDispatchToProps));

export default enhance(MenuButtonListEditor);
